/* ==========================================================================
   MEDICORA — external product feedback renderer
   Evidence anchor · source summary panels · recurring themes · homepage bridge
   --------------------------------------------------------------------------
   Renders MedicoraExternalFeedback. Hard rules enforced here, not in copy:

   - Nothing rendered by this file is ever called a Medicora review, a customer
     of ours, or a verified purchase.
   - A source's figures render only when exactProductMatchConfirmed,
     reuseAuthorizationConfirmed and displayAllowed are all true.
   - Sources are never averaged together — each keeps its own panel, its own
     count label, its own visible link and its own last-checked date.
   - No schema.org review markup is emitted for external feedback.
   - Third-party imagery renders only with a written permission record.
   ========================================================================== */
(function () {
  "use strict";

  /* On the Shopify theme every Medicora section injects this file, so on a
     page carrying eighteen sections it would run eighteen times. The render is
     idempotent (each target's innerHTML is replaced), but doing it once is
     both faster and easier to reason about. */
  if (window.MedicoraFeedbackReady) return;
  window.MedicoraFeedbackReady = true;

  var DATA = window.MedicoraExternalFeedback || { products: {}, externalReviewMedia: [] };
  var VARY = "Individual experiences vary.";

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function formatDate(iso) {
    if (!iso) return "";
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  /* a source may only be shown with numbers when all three flags are true */
  function isPublishable(source) {
    return !!(
      source &&
      source.displayAllowed &&
      source.exactProductMatchConfirmed &&
      source.reuseAuthorizationConfirmed &&
      typeof source.averageRating === "number" &&
      typeof source.ratingOrReviewCount === "number"
    );
  }

  function publishable(product) {
    return (product.sources || []).filter(isPublishable);
  }

  function Stars(rating) {
    var full = Math.round(Number(rating) || 0);
    var out = '<span class="stars stars--source" aria-hidden="true">';
    for (var i = 1; i <= 5; i++) {
      out +=
        '<svg viewBox="0 0 24 24" class="' + (i <= full ? "on" : "off") + '">' +
        '<path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.7l5.8-.8z"/></svg>';
    }
    return out + "</span>";
  }

  function sourceLink(source, label) {
    return (
      '<a class="source-link" href="' + esc(source.sourceUrl) +
      '" target="_blank" rel="noopener noreferrer">' + esc(label || "View source") +
      '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M14 4h6v6"/><path d="M20 4l-8.5 8.5"/><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>' +
      '<span class="sr-only"> (opens in a new tab)</span></a>'
    );
  }

  /* ------------------------------------------------------ source panel --- */
  function SourcePanel(source) {
    var head =
      '<div class="source-panel__head">' +
      '<span class="source-panel__name">' + esc(source.sourceName) + "</span>" +
      sourceLink(source) +
      "</div>";

    if (!isPublishable(source)) {
      /* No verified figure to show. Say what the source is, link it, and stop —
         no score, no count, no themes, no implied proof. */
      return (
        '<article class="source-panel source-panel--link-only">' +
        head +
        '<p class="fine-print">Buyer feedback for this product is published on this listing. ' +
        "It was written by that listing’s buyers, not submitted to Medicora, so we don’t reproduce " +
        "its rating here — read it at the source.</p>" +
        (source.exactProductIdentifier
          ? '<p class="source-panel__id">Listing reference: ' + esc(source.exactProductIdentifier) + "</p>"
          : "") +
        "</article>"
      );
    }

    var dist = source.distribution
      ? '<div class="source-panel__dist">' +
        [5, 4, 3, 2, 1]
          .map(function (star) {
            var n = Number(source.distribution[star] || 0);
            var pct = source.ratingOrReviewCount
              ? Math.round((n / source.ratingOrReviewCount) * 100)
              : 0;
            return (
              '<div class="dist-row dist-row--static"><span class="dist-star">' + star +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.7l5.8-.8z"/></svg></span>' +
              '<span class="dist-bar"><span style="width:' + pct + '%"></span></span>' +
              '<span class="dist-n">' + n + " <em>" + pct + "%</em></span></div>"
            );
          })
          .join("") +
        "</div>"
      : "";

    return (
      '<article class="source-panel">' +
      head +
      '<p class="source-panel__score">' +
      Stars(source.averageRating) +
      '<strong>' + Number(source.averageRating).toFixed(1) + " / 5</strong>" +
      '<span class="source-panel__count">' + source.ratingOrReviewCount + " " +
      esc(source.countLabel || "reviews") + "</span>" +
      "</p>" +
      (typeof source.recommendationRate === "number"
        ? '<p class="fine-print">' + source.recommendationRate + "% of reviewers on this listing say they would recommend it.</p>"
        : "") +
      dist +
      '<p class="source-panel__meta">Figures shown as published on this listing' +
      (source.exactProductIdentifier ? " (" + esc(source.exactProductIdentifier) + ")" : "") +
      ", last checked " + esc(formatDate(source.checkedAt)) + ".</p>" +
      "</article>"
    );
  }

  /* ----------------------------------------------------------- themes ---- */
  function Themes(product) {
    var themes = [];
    publishable(product).forEach(function (source) {
      (source.recurringThemes || []).forEach(function (theme) {
        themes.push({ theme: theme, source: source });
      });
    });
    if (!themes.length) return "";
    return (
      '<h3 class="themes-title">Frequently mentioned</h3>' +
      '<div class="theme-row">' +
      themes
        .slice(0, 4)
        .map(function (entry) {
          var t = entry.theme;
          return (
            '<article class="theme-card">' +
            "<h4>" + esc(t.title) + "</h4>" +
            "<p>" + esc(t.summary) + "</p>" +
            '<p class="theme-card__src">Recurring feedback on ' + esc(entry.source.sourceName) + "</p>" +
            "</article>"
          );
        })
        .join("") +
      "</div>"
    );
  }

  /* ----------------------------------------------- authorized media ------ */
  function Media(productKey) {
    var media = (DATA.externalReviewMedia || []).filter(function (m) {
      return m.productId === productKey && m.writtenReusePermissionConfirmed && m.permissionRecordReference;
    });
    if (!media.length) return "";
    return (
      '<h3 class="themes-title">Photos published with the original feedback</h3>' +
      '<div class="review-media__row">' +
      media
        .map(function (m, i) {
          return (
            '<figure class="external-media__item">' +
            '<span class="product-media"><img src="' + esc(m.originalMediaUrl) + '" alt="' + esc(m.altText) +
            '" loading="lazy"></span>' +
            "<figcaption>" + esc(m.photographerOrReviewerAttribution) + " · " +
            sourceLink(m, "source") + "</figcaption></figure>"
          );
        })
        .join("") +
      "</div>"
    );
  }

  /* ------------------------------------------------------ PDP section ---- */
  function renderSection(node, key) {
    var product = (DATA.products || {})[key];
    if (!product) return;
    var sources = product.sources || [];
    if (!sources.length) {
      node.hidden = true;
      return;
    }
    var anyPublishable = publishable(product).length > 0;

    node.innerHTML =
      '<div class="wrap-narrow">' +
      '<div class="section-head reveal">' +
      '<span class="eyebrow">Independent product feedback</span>' +
      '<h2 id="feedback-title">' +
      (anyPublishable ? "What buyers mention about this product" : "Independent feedback on the referenced product") +
      "</h2>" +
      '<p class="lede">This product is made as the ' + esc(product.productName) +
      ". The feedback below comes from the linked original product listing" +
      (sources.length > 1 ? "s" : "") +
      " for that product — it was written by buyers there and was not submitted to Medicora.</p>" +
      "</div>" +
      '<div class="source-grid reveal">' +
      sources.map(SourcePanel).join("") +
      "</div>" +
      '<div class="reveal">' + Themes(product) + Media(key) + "</div>" +
      '<p class="fine-print reveal" style="margin-top:20px">' + VARY +
      " Feedback describes personal experience and is not medical advice. Ingredients, size and directions for the product sold here are published in full " +
      '<a href="#ingredients">on this page</a>.</p>' +
      '<div class="feedback-actions reveal">' +
      '<button class="btn btn-light btn-sm" type="button" data-open-review-form="' + esc(key) + '">Share your experience</button>' +
      '<span class="fine-print">Reviews sent to Medicora are read before publication and never published automatically.</span>' +
      "</div></div>";
  }

  /* ---------------------------------------------------- evidence anchor -- */
  function renderAnchor(key) {
    var product = (DATA.products || {})[key];
    if (!product) return;
    qsa('[data-evidence-anchor="' + key + '"]').forEach(function (node) {
      var best = publishable(product)[0];
      var label = best
        ? Number(best.averageRating).toFixed(1) + "/5 on " + best.sourceName + " · " +
          best.ratingOrReviewCount + " " + (best.countLabel || "reviews")
        : "Independent buyer feedback";
      node.innerHTML =
        '<a class="evidence-anchor" href="#feedback">' +
        (best ? Stars(best.averageRating) : "") +
        "<span>" + esc(label) + "</span>" +
        '<span class="evidence-anchor__hint">' +
        (best ? "See the source" : "From the original product listing") + "</span></a>";
      qs(".evidence-anchor", node).addEventListener("click", function (e) {
        e.preventDefault();
        var section = qs("#feedback");
        if (!section) return;
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        var target = qs("#feedback-title", section) || section;
        target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      });
    });
  }

  /* --------------------------------------------------- homepage bridge --- */
  function renderBridge(node) {
    var entries = Object.keys(DATA.products || {});
    if (!entries.length) {
      node.hidden = true;
      return;
    }
    var STEP = {
      /* Real Shopify product URLs. These were static-site filenames carried over
         from the pre-Shopify build ("body-wash.html"), which resolved to
         /body-wash.html and returned 404 on every page that rendered this
         bridge. */
      "body-wash": { step: "Step 01 · Cleanse", url: "/products/kojic-acid-turmeric-brightening-body-wash", short: "Body Wash" },
      "peel-shot": { step: "Step 02 · Refine", url: "/products/hypochlorous-acid-rice-body-peel-shot", short: "Peel Shot" }
    };

    var cards = entries
      .map(function (key) {
        var product = DATA.products[key];
        var meta = STEP[key] || { step: "", url: "/", short: product.productName };
        var best = publishable(product)[0];
        var themes = [];
        publishable(product).forEach(function (s) {
          (s.recurringThemes || []).forEach(function (t) {
            themes.push(t);
          });
        });

        return (
          '<article class="bridge-card">' +
          '<span class="step-tag' + (key === "peel-shot" ? " step-tag--dark" : "") + '">' + esc(meta.step) + "</span>" +
          "<h3>" + esc(product.productName) + "</h3>" +
          (best
            ? '<p class="bridge-card__score">' + Stars(best.averageRating) +
              "<strong>" + Number(best.averageRating).toFixed(1) + " / 5</strong>" +
              "<span>" + best.ratingOrReviewCount + " " + esc(best.countLabel || "reviews") +
              " on " + esc(best.sourceName) + "</span></p>"
            : "") +
          (themes.length
            ? "<p>" + esc(themes[0].summary) + "</p>"
            : '<p class="fine-print">Buyer feedback for the original product is published on its own listing. ' +
              "It wasn’t submitted to Medicora, so we point you to the source instead of reprinting it.</p>") +
          '<a class="link-arrow" href="' + meta.url + '#feedback">Explore ' + esc(meta.short) + " feedback →</a>" +
          "</article>"
        );
      })
      .join("");

    node.innerHTML =
      '<div class="wrap">' +
      '<div class="section-head center reveal">' +
      '<span class="eyebrow">Independent product feedback</span>' +
      "<h2>Feedback on the referenced products</h2>" +
      '<p class="lede section-intro">Each step is made as its original product, and buyers of those originals have published their own feedback. We link you straight to it rather than reprinting it as our own.</p>' +
      "</div>" +
      '<div class="bridge-grid reveal">' + cards + "</div>" +
      '<p class="fine-print center reveal" style="margin-top:22px">' + VARY + "</p></div>";
  }

  /* ---------------------------------------------------------- startup ---- */
  function boot() {
    qsa("[data-external-feedback]").forEach(function (node) {
      var key = node.getAttribute("data-external-feedback");
      renderSection(node, key);
      renderAnchor(key);
    });

    var bridge = qs("[data-feedback-bridge]");
    if (bridge) renderBridge(bridge);

    if (window.MedicoraRevealRefresh) window.MedicoraRevealRefresh();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
